let md = `## 引子
1. 批判性思维的定义（上一讲概述的内容）![[附件/Pasted image 20230604020420.png]]
2. 理解是批判性思维的前提![[附件/Pasted image 20230604020534.png]]

## 一、理解的意义
1. 理解的质量决定我们生活的质量，理解就是生存。以下是一些具体的说明：
	1. 对别人的理解力
		1. 找对象不理解对方的需求，容易失败
	2. 对自我的理解力
		1. 不知道自己需要什么
		2. 领悟不到自己的人生使命
		3. 对自己的性格、特长、能力、弱势等没有自知之明
	3. 对生意的理解力
		1. 生意高手，只需要交流一两轮就能判断事情能否做成
	4. 对意义的理解力
		1. 意义不是客观存在的，而是理解的结果
		2. 风景的意义、语言的意义、工作的意义、人生的意义……与你自己的理解力有关
		3. 虚无主义：根本原因就在于我们对于人生意义的理解能力下降了
2. 例证：做题能力的高低，取决于对题干的理解能力，问题的内涵和要素就在题干中
	1. 问题：皇帝分家产，两个儿子都认为对方分多了。如果你是丞相，该如何解决？![[附件/Pasted image 20230604021333.png]]
		1. 方案一：把家产换算成钱，然后平分。（理论可行，实际难以操作。有些家产很难评估价值）
		2. 方案二：一个人分蛋糕，另外一个人来先选
		3. 答案：题干里面隐含了一些信息，大部分人可能没有理解：
			1. 家产已经分好了
			2. 哥哥认为弟弟的多，弟弟认为哥哥的多
			3. 也就是说，双方都更想要对方的那一份
			4. 所以解决方案很简单，既然双方都看中了对方的家产，交换一下就好了
	2. 理解是批判的前提，没有正确全面的理解，就没有真正的有效的批判。没有建立在正确全面的理解之上的批判，那就是骂街。
	3. 问题：儿子跟外公外婆产生矛盾，妈妈批评儿子对外公外婆不礼貌，儿子反驳：“我好好的跟他们说话，他们就是不听”。儿子这句话有什么问题吗？![[附件/Pasted image 20230604021245.png]]
		1. 分析一：你觉得自己好好说话，只是你自己的感觉，别人不一定是这么认为的
		2. 分析二：外公外婆也有好好跟你说话，为什么就不能是你听他们的呢？
		3. 答案：
			1. 首先，你的思想只是你的思想，别人没有义务必须接受，除非你以强大的逻辑严格地论证了你的思想。现在外公外婆没有接受你的想法，很可能是因为你的理由和论证在逻辑上还不够强大严密。
			2. 其次，你好好的说话，这只是你说话的态度。说话态度好并不能保证你的思想本身是对的，错误的思想很难因为态度正确而被人接受。我不可能以很好的态度对一个陌生人说：把你的钱全部给我吧。
			3. 最后，你好好的跟人说话，别人不听，你就可以生别人的气吗？为什么不是生自己的气，为什么不反省自己为什么不能很好的劝说别人接收自己的想法呢？

## 二、理解的过程
1. 游戏一：下面这张图里画了什么？![[附件/Pasted image 20230603233431.png]]
	1. “这张图里画了什么？”这个问题本身就是一个错误的问题，正确的问题只能是：“你从这张图里看出了什么？”或者“你把这张图看作什么？”
	2. 根据维特根斯坦的哲学，没有单纯的“看”，任何“看”都是“看作”。没有我们主观“图式”参与的“看作”，只能看到混沌的色块，而不能看出任何有意义的实物图形。
	3. 是我们把“大桥”或“帆船”的“图式”赋予了图中的色块。
	4. “看”图的过程，就是我们给它“赋形”的过程。图中的“大桥”、“帆船”其实是由我们主观的“图式”，也就是由我们的“思想”构建起来的。
	5. 康德：人为自然立法。
	6. 尼采：人最终在事物中找出的，无非就是他自己原先悄悄塞入其中的。
		1. 你塞的过程，你没有意识到
		2. 你找出来的过程，你很惊喜，你找到了
2. 游戏二：你看见了什么？![[附件/Pasted image 20230603234544.png]]
	1. 不能说“它就是B”或“它就是13”。只能说“它被我看作是B或13”
	2. 尼采：没有自在的“es ist”（它是什么），有的只是认为的“es gilt”（它被看作什么）
	3. 康德：物自体不可知
	4. 《周易·系辞》：仁者见之谓之仁，智者见之谓之智。（不应该简化为“仁者见仁智者见智”，少了这个“之”，原意变为唯心主义）
3. 理解的过程有很多我们主观的想象和引申。

4. 听觉游戏
	1. 语音口述：某商人刚关上店里的灯，一男子来到店堂并索要钱款，店主打开收银机，收银机内的东西被倒了出来而那个男子逃走了，一位警察很快接到报案。
	2. 以下12道小题，你认为对的打√，错的打×，不能确定的打“?”
		1. 店主将店堂内的灯关掉后，一男子到达。
		2. 抢劫者是一名男子。
		3. 来的那个男子没有索要钱款。
		4. 打开收银机的那个男子是店主。
		5. 店主倒出收银机中的东西后逃离。
		6. 故事中提到了收银机，但没说里面具体有多少钱。
		7. 抢劫者向店主索要钱款。
		8. 索要钱款的男子倒出收银机中的东西后，急忙离开。
		9. 抢劫者打开了收银机。
		10. 店堂灯关掉后，一个男子来了。
		11. 抢劫者没有把钱随身带走。
		12. 故事涉及三个人物：店主，一个索要钱款的男子，以及一个警察。
	3. 我的答案：
		1. ？
		2. 对
		3. 错
		4. ？店主性别未知
		5. ？未说明
		6. 对
		7. ？没有说明索要对象，可能是向服务员
		8. ？没有说明是谁倒出来的
		9. 错
		10. ？先关灯还是先来人，没有交代
		11. ？不确定
		12. ？也许还有其它人
	4. 参考答案
		1. ![[附件/Pasted image 20230604000413.png]]
	5. 答案解析：
		1. 不确定。某商人有可能不是后面的店主。
		2. 不确定。索要钱款不一定是抢劫。有可能是催债。也有可能是老板向店员索要今天的营业额。（容易把索要欠款理解为抢劫）
		3. 错。
		4. 不确定。店主的性别题干没有说。（容易脑补性别）
		5. 不确定。那个男子的指代不明确。
		6. 对。
		7. 不确定。描述的可能是男子抢劫店主的抢劫案，也可能是店主拿出收银机里的抢追打前来讨债的男子。（同2）
		8. 不确定。谁倒的收银机不能确定。离开时急忙还是不急忙，不能确定。
		9. 错。明确是店主打开收银机，店主在自己店里打开收银机不能称之为抢劫者。
		10. 对。**刚关上**灯，人来了。
		11. 不确定。是不是抢劫不能确定。有没有把钱带走不能确定。
		12. 不确定。商人可能是店员，也可能是店主。
5. 游戏总结：
	1. 我们的理解，不是简单的、被动的“照单全收”的接受过程
	2. 我们的理解往往是“浮想联翩”的主动加工过程
	3. 我们在加工过程中添加了很多题干没有的、实际上是我们自己主观的想象（我们对此毫无自知）
	4. 哲学术语：理解的过程就是视域融合的过程。（Vorurteil：先见、偏见）![[附件/Pasted image 20230604004516.png]]
	5. 思考：人出生后的第一个Vorurteil从哪里来？

## 三、理解的质量
1. 影响理解、导致理解偏差的因素：
	1. 文本因素：
		1. 陌生的语言或方言、口齿不清、字迹模糊、错字难字、语法错误、长难句等
		2. 表达上词不达意、条理混乱，缺少必要的分析和解释
	2. 环境因素：噪音、温度、电话铃、别人的打扰、催促、事件过于紧急等
	3. 读者因素
		1. 生理性因素：视力、听力、精力等
		2. 社会性因素：角色、身份、立场（自己人效应、敌人效应）等
		3. 心理性因素：需要、期待、兴趣、心境、记忆、想象、图式、第一印象、过往经历、对主题的熟悉程度等
		4. 技能性因素：理解技能太差也容易导致低劣的理解质量
2. 理解的结果
	1. 错误
		1. 不解：对意义生成的意图失败，它对自己的理解失败有明显的自知和自觉，所以没有回应和行动。
		2. 误解：生成了错误的意义，并且对自己的误解没有自知，同时还自以为是，因此容易引发错误的回应和行动。（不准确）
			1. 自以为是、不自知的误解（极大部分）
			2. 自知的误解（极小部分）
	2. 遗漏：你的理解漏掉了别人的一些重要意思（不全面）
		1. 无意的漏读
		2. 有意的断章取义
	3. 扩充：你的理解对别人做了过多的扩充、引申或联想
		1. 自作多情
3. 如何提升理解的质量
	1. 认真阅读：静心、认真是有效理解的前提。
	2. 写写画画：
		1. 听讲做笔记
		2. 阅读时写写画画：拉进你与文本之间的距离
		3. 写感想、做综述
	3. 多读几遍：所谓读书百遍，其义自见。
	4. 移情阅读
		1. 把自己想想成对方或作者，设身处地听和读。
		2. 我们如何才能对他的语气、态度、喜怒哀乐做到感同身受？
		3. 他当时的处境是怎样的？
		4. 我们能不能站在他的力场来理解问题？
	5. 提问分析：主动提问、分析和反思其思维的诸要素。
		1. 目的是什么？
		2. 讨论什么问题？
		3. 使用哪些概念？
		4. 怎样定义这些概念？
		5. 有没有歧义？
		6. 搜集的信息是否可靠全面？
		7. 有没有疑问和遗漏？
		8. 结论是什么？
		9. 结论怎么的来的？
		10. 理由是否充分？
		11. 得出结论的过程是否严密？
		12. 作者的背景资料？
		13. 立场、观念或偏见？
	6. 与人讨论
		1. 与作者讨论：您说的是不是这个意思？
		2. 与其他读者讨论：你怎么看？
	7. 学习理论
		1. 多读经济学、管理学、心理学、哲学等理论著作，有助于你公家深刻全面地理解相应的现象。
	8. 其它的方法：很多
4. 例子分析：小和尚撞钟![[附件/Pasted image 20230604014317.png]]
	1. 问题：在这个寓言故事最想要说明的一个道理是什么？
		1. 我的理解：
			1. 任何看上去简单的工作，都有很多技巧和要求
			2. 做事情不能简单做事情，要思考事情背后的原因和要求
		2. 老师参考答案：
			1. 领导在布置工作前，先要给下属讲清楚工作的意义和具体的要求
			2. 大家看问题的角度很可能只从小和尚视角，老主持也有他的问题（我的理解：大家是否默认老主持是有道理的，年轻的小和尚是容易犯错的？）
	2. 帮助大家全面理解的几个问题：
		1. 最后一句“他自己”指代的是谁？【非常关键的信息，大家可能都误读了】
		2. 谁是故事的主角？
		3. “很不服气”说明什么问题？
		4. “信服口服”说明什么问题？
		5. 谁该为小和尚撞钟不称职负主要责任？
		6. 老主持身上最大的问题是什么？
		7. 故事最想要说明的一个道理是什么？

## 四、本讲总结
1. 理解的过程是充满主观性的主管文本视域融合的过程
2. 在此基础上，努力自掘地减少我们理解过程中成问题的先见和主观的想象，并通过各种理解的技巧和方法，来提高我们的理解质量，力求做到理解的客观、准确和全面。
3. 这样才能有真正的批判性思维。`

module.exports = {
  title: '理解',
  author: 'gzshi',
  post: '2023-06-08 22:50',
  tags: ['博弈', '管理', '胡思乱想'],
  summary: md,
  summaryCompiler: 'md',
  more: false
}


// WEBPACK FOOTER //
// ./src/blog/20230608_critical_thinking/p4.js